// 裁判文书：跨页面共享的状态与工具（列表 / 详情数据均来自后端接口）。
// 文书类型枚举与当前选中类型为模块级共享状态；另提供 GetPage 记录映射、
// 状态码映射与文件 / 状态展示工具
import { ref } from "vue";
import { getGenerationTypeList, downloadJudgmentDoc } from "@/api/judicativePaper";
import FileHandler from "@/utils/fileHandler";

// 文书类型：默认值兜底，进入页面后由 GetDocTypeEnumList 接口刷新
export const DOC_TYPES = ref([
  "判决书",
  "调解书",
  "裁定书",
  "行政复议决定书",
  "劳动仲裁书",
]);
// 类型枚举原始项（label 展示 + value 上传），接口返回后填充
export const docTypeItems = ref([]);
export const ACCEPT_EXTS = ["doc", "docx", "pdf", "xls", "xlsx", "jpg", "jpeg", "png"];
// 首页案件材料仅支持压缩包上传（补充材料弹窗仍用 ACCEPT_EXTS）
export const ARCHIVE_EXTS = ["zip", "rar", "7z"];

// 首页（最近文书）与全部记录页各自记住的文书类型
export const activeType = ref("判决书");
export const activeHistoryType = ref("判决书");

// 拉取文书类型枚举（GetDocTypeEnumList）：成功后替换 DOC_TYPES（展示文案）
// 与 docTypeItems（含 value，供上传接口 documentType），
// 当前选中类型不在新列表时回落到第一项；失败保留默认值不打断页面。
// promise 缓存：首页 / 列表页各自调用也只发一次请求
let docTypesPromise = null;
export function fetchDocTypes() {
  if (!docTypesPromise) {
    docTypesPromise = (async () => {
      try {
        const res = await getGenerationTypeList();
        const data = (res && (res.data || res)) || {};
        const arr = Array.isArray(data)
          ? data
          : data.items || data.list || [];
        // 枚举项可能是字符串或 { name/label/value } 对象：
        // label 取展示文案，value 取上传参数（字符串项 value 即其本身）
        const items = arr
          .map((x) =>
            typeof x === "string"
              ? { label: x, value: x }
              : {
                  label:
                    x.name ?? x.label ?? x.value ?? x.typeName ?? x.description,
                  value: x.value ?? x.id ?? x.name ?? x.label,
                },
          )
          .filter((x) => x.label);
        if (items.length) {
          docTypeItems.value = items;
          const labels = items.map((x) => x.label);
          DOC_TYPES.value = labels;
          if (!labels.includes(activeType.value)) activeType.value = labels[0];
          if (!labels.includes(activeHistoryType.value))
            activeHistoryType.value = labels[0];
        }
      } catch (err) {
        console.error("[JudicativePaper] getGenerationTypeList error:", err);
      }
    })();
  }
  return docTypesPromise;
}

// 按展示文案取枚举 value（importDossier / GetPage / GenerateJudgment 的类型参数用）；
// 未命中：判决书兜底 1（与后端枚举一致），其余回落原文案
export function docTypeValue(typeName) {
  const hit = docTypeItems.value.find((i) => i.label === typeName);
  if (hit) return hit.value;
  if (typeName === "判决书") return 1;
  return typeName;
}

// 按枚举 value 反查展示文案（列表记录的 documentType → 行内 type 展示用）；
// 宽松比较兼容数字 / 字符串码值，未命中回落原值（空值由调用方兜底）
export function docTypeLabel(value) {
  if (value === undefined || value === null || value === "") return "";
  const hit = docTypeItems.value.find((i) => String(i.value) === String(value));
  return hit ? hit.label : value;
}

// 正在查看的任务快照：列表行（接口数据）跳结果页时暂存，结果页以此渲染
// （刷新后丢失，回落「未找到记录」兜底页）
export const viewingTask = ref(null);

// 下载文书并保存到本地（任务行「下载」与结果页「下载文书」共用）。
// 调用方自行 catch 做 toast 兜底
export async function saveJudgmentDoc(t) {
  const blob = await downloadJudgmentDoc(t.docId ?? t.id);
  FileHandler.saveFile(blob, judicialDocFileName(t));
}

// ---------- 文件工具 ----------
export function formatSize(s) {
  if (s < 1024) return s + " B";
  if (s < 1024 * 1024) return Math.round(s / 1024) + " KB";
  return (s / 1024 / 1024).toFixed(1) + " MB";
}
export function fileExt(name) {
  return (name.split(".").pop() || "FILE").toUpperCase();
}
export function fileClass(name) {
  const n = name.toLowerCase();
  if (/\.(zip|rar|7z)$/.test(n)) return "archive";
  if (n.endsWith(".pdf")) return "pdf";
  if (n.endsWith(".xls") || n.endsWith(".xlsx")) return "excel";
  if (/\.(jpg|jpeg|png)$/.test(n)) return "image";
  return "word";
}
export function fileLabel(name) {
  const cls = fileClass(name);
  if (cls === "archive") return (name.split(".").pop() || "zip").toUpperCase();
  return cls === "pdf" ? "PDF" : cls === "excel" ? "XLS" : cls === "image" ? "IMG" : "W";
}

// ---------- 任务工具 ----------
// 内部状态 key → tag 配色/默认文案/是否转圈（后端状态码先经 mapPageItem 的
// STATUS_CODE_MAP 映射为内部 key；statusText 有接口文案时优先展示）
const STATUS_MAP = {
  done: { cls: "done", text: "生成完成" },
  missing: { cls: "missing", text: "资料待补充" },
  parsing: { cls: "parsing", text: "材料解析中", running: true },
  generating: { cls: "generating", text: "生成中", running: true },
  failed: { cls: "failed", text: "生成失败" },
};

export function statusInfo(t) {
  let base = STATUS_MAP[t.status];
  // 状态码未命中时按接口文案 generateStatusText 的关键词推断配色，
  // 保证不同状态仍有颜色区分；都无法识别回落中性灰并直接展示状态码
  if (!base) {
    const s = t.statusText || "";
    if (/完成/.test(s)) base = { cls: "done" };
    else if (/失败/.test(s)) base = { cls: "failed" };
    else if (/补充|缺少/.test(s)) base = { cls: "missing" };
    else if (/解析|生成中|处理中|进行中/.test(s))
      base = { cls: "generating", running: true };
  }
  if (!base)
    return { cls: "unknown", running: false, text: String(t.status ?? "") };
  return {
    ...base,
    running: !!base.running,
    text: t.statusText || base.text,
  };
}
export function totalFiles(t) {
  return (t.inherited?.length || 0) + (t.original?.length || 0) + (t.supplements || []).reduce((n, b) => n + b.files.length, 0);
}
export function allTaskFiles(t) {
  return [...(t.inherited || []), ...(t.original || []), ...(t.supplements || []).flatMap((b) => b.files || [])].map((f) => ({ name: f.name, size: f.size }));
}

// 各文书类型对应的成品文件名（结果页下载与 AI 对话文件卡片共用）
export function judicialDocFileName(t) {
  const map = { 判决书: "民事判决书.docx", 调解书: "民事调解书.docx", 裁定书: "民事裁定书.docx", 行政复议决定书: "行政复议决定书.docx", 劳动仲裁书: "仲裁裁决书.docx" };
  return map[t?.type] || `${t?.type || "裁判文书"}.docx`;
}

// ---------- GetPage 返回 → 任务行结构（首页最近列表 / 全部记录页共用） ----------
// 后端 generateStatus 状态码 → 内部状态 key（TaskRow 按钮 / 弹窗逻辑按 key 分支）：
// 0 材料解析中 / 1 资料待补充 / 2 生成中 / 3 生成完成 / 4 生成失败
const STATUS_CODE_MAP = {
  0: "parsing",
  1: "missing",
  2: "generating",
  3: "done",
  4: "failed",
};

// 记录字段名以后端实际返回为准，缺省给兜底值；
// 材料明细留空（行内「共 N 份」与材料弹窗待接 GetMaterials 后填充）
export function mapPageItem(item) {
  return {
    id: item.id ?? item.documentId ?? item.docId,
    docId: item.documentId ?? item.docId ?? item.id,
    // documentType 为文书类型枚举 value，经 docTypeLabel 反查展示文案
    // （与类型 tab 文案同源）；空值回落「判决书」
    type: docTypeLabel(item.documentType ?? item.docType ?? item.type) || "判决书",
    name: item.documentName || "未命名文书",
    // generateStatus 状态码经 STATUS_CODE_MAP 映射为内部状态 key；
    // ?? 取值保证码值 0 不被 || 吞掉；
    // generateStatusText 为接口给的状态展示文案，状态胶囊优先展示
    status:
      STATUS_CODE_MAP[item.generateStatus ?? item.status] ??
      item.status ??
      "done",
    statusText: item.generateStatusText || "",
    time: item.createTime || item.createdAt || item.createdTime || "",
    original: Array.isArray(item.materials) ? item.materials : [],
    supplements: [],
  };
}

// 解包 GetPage 返回：兼容 { code, data } 外层与 items/list/records/rows 列表字段
export function unwrapPage(res) {
  const data = (res && (res.data || res)) || {};
  const list =
    data.items ||
    data.list ||
    data.records ||
    data.rows ||
    (Array.isArray(data) ? data : []);
  const total = Number(
    data.total ?? data.totalCount ?? data.count ?? list.length,
  );
  return { list, total };
}

